import React from 'react';
import { Mail, Phone } from 'lucide-react';
import SEO from '../components/SEO';

const RefundPage = () => {
  return (
    <div className="min-h-screen pt-20">
      <SEO
        title="Refund Policy | Cancellation Terms | Equi Pulse Research"
        description="Understand Equi Pulse Research refund and cancellation policy. Learn about our refund process, eligibility criteria, and timeline for service cancellations."
        url="/refund-policy"
      />
      <div className="container mx-auto px-4 py-12 max-w-4xl">
        <h1 className="text-4xl font-bold text-amber-400 mb-8">Refund Policy</h1>
        
        <div className="prose prose-invert prose-amber max-w-none text-gray-300">
          <p className="mb-8">
            We prioritize our clients and are committed to providing high-quality research and support services. Our goal is to maintain complete transparency regarding our services, terms, and refund practices.
          </p>

          <h2 className="text-2xl font-bold text-amber-400 mt-8 mb-4">Nature of Services & Market Risk</h2>
          <p className="mb-8">
            Trading and investment in the securities market are subject to market risks. The research reports, recommendations, and calls provided by us <strong>do not</strong> carry any assurance or guarantee of returns.
          </p>

          <p className="mb-8">
            Clients acknowledge that any losses incurred based on our research or recommendations are solely their responsibility, and <strong>no claims for losses or compensation shall be entertained</strong>.
          </p>

          <h2 className="text-2xl font-bold text-amber-400 mt-8 mb-4">No Guarantee on Calls</h2>
          <p className="mb-8">
            We do not offer guarantee on any calls or recommendations. Service outcomes may vary due to market conditions, and dissatisfaction with performance or results does not entitle the client to a full refund.
          </p>

          <h2 className="text-2xl font-bold text-amber-400 mt-8 mb-4">Cancellation & Refund Policy</h2>
          <p className="mb-4">
            Once a subscription is activated and payment is completed, refunds are applicable only on a pro-rata basis for the unused portion of the subscription period.
          </p>

          <p className="mb-4">
            No refund will be provided for the period of services already availed, regardless of the client's satisfaction level or trading outcome.
          </p>

          <p className="mb-4">Pro-rata refunds, if applicable, will be calculated after deducting:</p>
          <ul className="list-disc list-inside mb-8 space-y-2 ml-4">
            <li>Charges for services already availed</li>
            <li>Applicable taxes</li>
            <li>Administrative and processing fees</li>
          </ul>

          <p className="mb-8">
            <strong>All sales are final for the availed service period.</strong>
          </p>

          <h2 className="text-2xl font-bold text-amber-400 mt-8 mb-4">Compliance with SEBI Guidelines</h2>
          <p className="mb-8">
            All refund and cancellation requests are handled strictly in accordance with SEBI notifications, circulars, and applicable laws. As per SEBI regulations, refunds are permitted only for the unused portion of the subscription period.
          </p>

          <h2 className="text-2xl font-bold text-amber-400 mt-8 mb-4">Client Support & Assistance</h2>
          <p className="mb-8">
            If a client is dissatisfied for any unforeseen reason, they are encouraged to contact our support team. We will make every reasonable effort to address concerns and improve satisfaction. However, this does not alter or override the refund policy, which remains strictly pro-rata.
          </p>

          <h2 className="text-2xl font-bold text-amber-400 mt-8 mb-4">Important Instructions Before Payment</h2>
          <p className="mb-4">Before making any payment, clients are strongly advised to:</p>
          <ul className="list-disc list-inside mb-8 space-y-2 ml-4">
            <li>Review complete information regarding our services and support</li>
            <li>Read and understand our Terms & Conditions, Privacy Policy, and Refund Policy</li>
            <li>Acknowledge that there is no return or profit guarantee</li>
            <li>Understand that refunds are strictly pro-rata and subject to SEBI rules</li>
            <li>Accept that no refund is possible for services already availed</li>
          </ul>

          <div className="bg-amber-900/20 border border-amber-500/30 p-6 rounded-lg my-8">
            <h3 className="text-xl font-bold text-amber-400 mb-3">Final Declaration</h3>
            <ul className="space-y-3">
              <li>There is no provision for a full refund under any circumstances.</li>
              <li>Complaints regarding service quality during the paid period do not qualify for refunds or compensation.</li>
              <li><strong>By making a payment, the client confirms that they have read, understood, and agreed to all terms, disclaimers, and policies.</strong></li>
            </ul>
          </div>

          <h2 className="text-2xl font-bold text-amber-400 mt-8 mb-4">Contact Us</h2>
          <p className="mb-4">For any queries or support, please contact:</p>
          
          <div className="bg-slate-800/50 p-6 rounded-lg border border-amber-500/20 mt-6">
            <div className="flex flex-col gap-3">
              <a href="tel:+918982892301" className="flex items-center gap-2 text-amber-400 hover:text-amber-300">
                <Phone className="w-5 h-5" aria-hidden="true" />
                +91-8982892301
              </a>
              <a href="mailto:care@equipulseresearch.com" className="flex items-center gap-2 text-amber-400 hover:text-amber-300">
                <Mail className="w-5 h-5" aria-hidden="true" />
                care@equipulseresearch.com
              </a>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default RefundPage;